<template>
    <section class="section">
        <div class="container">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("pricing.page_title") }}</h1>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <div class="tabs">
                        <ul>
                            <li :class="{ 'is-active': activeTab === 'pricing' }">
                                <a @click="setActiveTab('pricing')">
                                    <span class="icon is-small"><i class="fas fa-money-bill-wave"></i></span>
                                    <span>{{ translate("pricing.tab_pricing") }}</span>
                                </a>
                            </li>
                            <li :class="{ 'is-active': activeTab === 'coupons' }">
                                <a @click="setActiveTab('coupons')">
                                    <span class="icon is-small"><i class="fas fa-ticket-alt"></i></span>
                                    <span>{{ translate("pricing.tab_discount_coupons") }}</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div v-show="activeTab === 'pricing'">
                <Notification v-if="!store.isFederationApproved" class="is-warning">
                    {{ translate("pricing.online_payments_disabled_unapproved") }}
                </Notification>

                <div class="columns">
                    <div class="column">
                        <div class="buttons">
                            <button class="button is-primary" @click="openAddPricingModal">
                                <span class="icon"> <i class="fas fa-plus"></i></span>
                                <span>
                                    {{ translate("pricing.add_new") }}
                                </span>
                            </button>

                            <button class="button" :disabled="!store.isFederationApproved" @click="switchEnableOnlinePaymentsSetting">
                                <span class="icon">
                                    <i v-if="store.enableOnlinePaymentsSetting" class="fa fa-check-square" />
                                    <i v-else class="fa fa-square" />
                                </span>
                                <span>{{ translate("pricing.enable_online_payments") }}</span>
                            </button>

                            <button class="button" :disabled="!store.isFederationApproved" @click="openSetupCurrenciesModal">
                                <span class="icon">
                                    <i class="fas fa-edit"></i>
                                </span>
                                <span>{{ translate("pricing.payment_gateway_cfg_btn") }}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <PricingDefinitionListing />
            </div>

            <div v-show="activeTab === 'coupons'">
                <CouponManagement :event-id="eventId" :event-timezone="eventTimezone" />
            </div>
        </div>

        <PricingModal ref="pricingModal" :event-id="eventId" :event-timezone="eventTimezone" />
        <ConfigurePaymentGatewayModal ref="configurePaymentGatewayModal" />
    </section>
</template>

<script setup lang="ts">
    import { onMounted, ref } from "vue";
    import { translate } from "../Common/translations";
    import history from "../Common/history";
    import Notification from "../Common/Notification.vue";
    import PricingModal from "./PricingModal.vue";
    import { eventBus, OPEN_ADD_PRICING_MODAL, OPEN_CONFIGURE_PAYMENT_GATEWAY_MODAL, OPEN_EDIT_PRICING_MODAL } from "./eventBus";
    import PricingDefinitionListing from "./PricingDefinitionListing.vue";
    import ConfigurePaymentGatewayModal from "./PaymentGatewayConfig/ConfigurePaymentGatewayModal.vue";
    import CouponManagement from "./Coupons/CouponManagement.vue";
    import categoriesApi from "../Api/Categories/categories";
    import eventSettingsApi from "../Api/eventSettingsApi";
    import { successToast } from "../Common/toasts";
    import { handleError } from "../Common/errorHandler";
    import { usePaymentGatewayConfigStore, usePricingSetupStore } from "./pricingSetupApp";

    const historyInstance = history.getInstance(window);

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
        userId: number;
        przelewy24SignUpUrl: string;
        isFederationApproved: boolean;
    }>();

    const store = usePricingSetupStore();
    const paymentGatewayConfigStore = usePaymentGatewayConfigStore();

    store.userId = props.userId;
    store.eventId = props.eventId;
    store.eventTimezone = props.eventTimezone;
    store.isFederationApproved = props.isFederationApproved;
    paymentGatewayConfigStore.przelewy24SignUpUrl = props.przelewy24SignUpUrl;

    const getInitialTab = (): "pricing" | "coupons" => {
        const urlParams = new URLSearchParams(window.location.search);
        const tab = urlParams.get("tab");
        return tab === "coupons" ? "coupons" : "pricing";
    };

    const activeTab = ref<"pricing" | "coupons">(getInitialTab());

    const setActiveTab = (tab: "pricing" | "coupons") => {
        activeTab.value = tab;
        historyInstance.setQueryParam("tab", tab);
    };

    const openSetupCurrenciesModal = () => {
        eventBus.$emit(OPEN_CONFIGURE_PAYMENT_GATEWAY_MODAL);
    };

    const openAddPricingModal = () => {
        eventBus.$emit(OPEN_ADD_PRICING_MODAL);
    };

    const switchEnableOnlinePaymentsSetting = () => {
        const newValue = !store.enableOnlinePaymentsSetting;

        return eventSettingsApi
            .updateSetting(props.eventId, "enableOnlinePayments", newValue)
            .then(() => {
                if (newValue) {
                    successToast(translate("pricing.online_payments_enabled"));
                } else {
                    successToast(translate("pricing.online_payments_disabled"));
                }

                return fetchEnableOnlinePaymentsSetting();
            })
            .catch(handleError);
    };

    const fetchEnableOnlinePaymentsSetting = () => {
        return eventSettingsApi
            .getSingleSetting(props.eventId, "enableOnlinePayments")
            .then(setting => {
                store.enableOnlinePaymentsSetting = setting.value as boolean;
            })
            .catch(handleError);
    };

    const pricingModal = ref<InstanceType<typeof PricingModal> | null>(null);
    const configurePaymentGatewayModal = ref<InstanceType<typeof ConfigurePaymentGatewayModal> | null>(null);

    onMounted(() => {
        eventBus.$on(OPEN_ADD_PRICING_MODAL, () => {
            pricingModal.value?.open();
        });
        eventBus.$on(OPEN_CONFIGURE_PAYMENT_GATEWAY_MODAL, () => {
            configurePaymentGatewayModal.value?.open();
        });
        eventBus.$on(OPEN_EDIT_PRICING_MODAL, pricingDefinition => {
            pricingModal.value?.openForEdit(pricingDefinition);
        });

        return fetchEnableOnlinePaymentsSetting()
            .then(() => {
                return categoriesApi.getAllLevels(props.eventId);
            })
            .then(categories => {
                store.allLevelsCategories = categories;
            })
            .catch(handleError);
    });
</script>

<style scoped></style>
